import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  BLOCK_PALETTE,
  BLOCK_TYPES,
  createDefaultBlock,
  moveBlock,
  newChecklistItemId,
  newTableColumnId,
} from "./formDocumentSchema";
import { FormDocumentBlock } from "./FormDocumentBlock";
import { FormDocumentBlockWrapper } from "./FormDocumentBlockWrapper";

function BlockPropertiesPanel({ block, onChange, t }) {
  if (!block) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-sm text-slate-500">
        {t("documentForms.builder.select_block")}
      </div>
    );
  }

  const props = block.props || {};
  const update = (patch) => onChange({ ...block, props: { ...props, ...patch } });

  const inputClass =
    "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900";

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {t(`documentForms.blocks.${block.type}`, block.type)}
      </div>

      {block.type === BLOCK_TYPES.HEADING ? (
        <label className="block text-sm text-slate-600">
          {t("documentForms.props.level")}
          <select
            value={props.level || 2}
            onChange={(e) => update({ level: Number(e.target.value) })}
            className={`${inputClass} mt-1`}
          >
            <option value={1}>H1</option>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </select>
        </label>
      ) : null}

      {[BLOCK_TYPES.TEXT_FIELD, BLOCK_TYPES.TEXTAREA, BLOCK_TYPES.NUMBER, BLOCK_TYPES.DATE, BLOCK_TYPES.CHECKBOX, BLOCK_TYPES.SELECT, BLOCK_TYPES.TABLE].includes(block.type) ? (
        <>
          <label className="block text-sm text-slate-600">
            {t("documentForms.props.label")}
            <input
              type="text"
              value={props.label || ""}
              onChange={(e) => update({ label: e.target.value })}
              className={`${inputClass} mt-1`}
            />
          </label>
          {block.type === BLOCK_TYPES.TEXT_FIELD ? (
            <label className="block text-sm text-slate-600">
              {t("documentForms.props.placeholder")}
              <input
                type="text"
                value={props.placeholder || ""}
                onChange={(e) => update({ placeholder: e.target.value })}
                className={`${inputClass} mt-1`}
              />
            </label>
          ) : null}
          {block.type !== BLOCK_TYPES.CHECKBOX ? (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(props.required)}
                onChange={(e) => update({ required: e.target.checked })}
              />
              {t("documentForms.props.required")}
            </label>
          ) : null}
        </>
      ) : null}

      {block.type === BLOCK_TYPES.CALLOUT ? (
        <label className="block text-sm text-slate-600">
          {t("documentForms.props.variant")}
          <select
            value={props.variant || "info"}
            onChange={(e) => update({ variant: e.target.value })}
            className={`${inputClass} mt-1`}
          >
            <option value="info">{t("documentForms.props.info")}</option>
            <option value="warning">{t("documentForms.props.warning")}</option>
          </select>
        </label>
      ) : null}

      {block.type === BLOCK_TYPES.SELECT ? (
        <label className="block text-sm text-slate-600">
          {t("documentForms.props.options")}
          <textarea
            rows={4}
            value={(props.options || []).join("\n")}
            onChange={(e) =>
              update({
                options: e.target.value.split("\n").filter(Boolean),
              })
            }
            className={`${inputClass} mt-1`}
          />
        </label>
      ) : null}

      {block.type === BLOCK_TYPES.CHECKLIST ? (
        <div className="space-y-2">
          <div className="text-sm text-slate-600">{t("documentForms.props.items")}</div>
          {(props.items || []).map((item, index) => (
            <input
              key={item.id || index}
              type="text"
              value={item.label || ""}
              onChange={(e) => {
                const items = [...(props.items || [])];
                items[index] = { ...items[index], label: e.target.value };
                update({ items });
              }}
              className={inputClass}
            />
          ))}
          <button
            type="button"
            onClick={() =>
              update({
                items: [
                  ...(props.items || []),
                  { id: newChecklistItemId(), label: "Nytt punkt" },
                ],
              })
            }
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            + {t("documentForms.props.add_item")}
          </button>
        </div>
      ) : null}

      {block.type === BLOCK_TYPES.TABLE ? (
        <div className="space-y-3">
          {(props.columns || []).map((col, index) => (
            <div key={col.id} className="rounded-xl border border-slate-100 p-3">
              <input
                type="text"
                value={col.label || ""}
                onChange={(e) => {
                  const columns = [...(props.columns || [])];
                  columns[index] = { ...columns[index], label: e.target.value };
                  update({ columns });
                }}
                className={`${inputClass} mb-2`}
                placeholder={t("documentForms.props.column_label")}
              />
              <select
                value={col.type || "text"}
                onChange={(e) => {
                  const columns = [...(props.columns || [])];
                  columns[index] = { ...columns[index], type: e.target.value };
                  update({ columns });
                }}
                className={inputClass}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="checkbox">Checkbox</option>
              </select>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              update({
                columns: [
                  ...(props.columns || []),
                  { id: newTableColumnId(), label: "Kolonne", type: "text" },
                ],
              })
            }
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            + {t("documentForms.props.add_column")}
          </button>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={props.allowAddRows !== false}
              onChange={(e) => update({ allowAddRows: e.target.checked })}
            />
            {t("documentForms.props.allow_add_rows")}
          </label>
        </div>
      ) : null}
    </div>
  );
}

export function FormDocumentBuilder({ blocks = [], onChange }) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(blocks[0]?.id || null);

  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedId) || null,
    [blocks, selectedId]
  );

  const updateBlocks = (next) => {
    onChange?.(next);
    if (selectedId && !next.find((b) => b.id === selectedId)) {
      setSelectedId(next[0]?.id || null);
    }
  };

  const updateBlock = (index, patch) => {
    const next = blocks.map((b, i) => (i === index ? patch : b));
    updateBlocks(next);
  };

  const addBlock = (type) => {
    const block = createDefaultBlock(type);
    updateBlocks([...blocks, block]);
    setSelectedId(block.id);
  };

  const removeBlock = (index) => {
    updateBlocks(blocks.filter((_, i) => i !== index));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    updateBlocks(moveBlock(blocks, from, to));
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[220px_minmax(0,1fr)_280px]">
      <aside className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("documentForms.builder.blocks")}
        </div>
        <div className="space-y-1">
          {BLOCK_PALETTE.map((item) => (
            <button
              key={item.type}
              type="button"
              onClick={() => addBlock(item.type)}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <PlusIcon className="h-4 w-4 text-slate-400" />
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      </aside>

      <div
        className="min-h-[520px] overflow-visible rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm"
        onClick={() => setSelectedId(null)}
        role="presentation"
      >
        {blocks.length === 0 ? (
          <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center text-sm text-slate-500">
            <p>{t("documentForms.builder.empty")}</p>
            <p className="mt-1">{t("documentForms.builder.empty_hint")}</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="form-document-blocks">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="mx-auto max-w-2xl space-y-2"
                >
                  {blocks.map((block, index) => (
                    <Draggable
                      key={String(block.id)}
                      draggableId={String(block.id)}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <FormDocumentBlockWrapper
                          mode="builder"
                          selected={selectedId === block.id}
                          onSelect={() => setSelectedId(block.id)}
                          onMoveUp={() => updateBlocks(moveBlock(blocks, index, index - 1))}
                          onMoveDown={() => updateBlocks(moveBlock(blocks, index, index + 1))}
                          onDelete={() => removeBlock(index)}
                          canMoveUp={index > 0}
                          canMoveDown={index < blocks.length - 1}
                          innerRef={dragProvided.innerRef}
                          draggableProps={dragProvided.draggableProps}
                          dragHandleProps={dragProvided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                        >
                          <FormDocumentBlock
                            block={block}
                            mode="builder"
                            onPropsChange={(props) =>
                              updateBlock(index, { ...block, props })
                            }
                          />
                        </FormDocumentBlockWrapper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <aside>
        <BlockPropertiesPanel
          block={selectedBlock}
          t={t}
          onChange={(nextBlock) => {
            const index = blocks.findIndex((b) => b.id === nextBlock.id);
            if (index >= 0) updateBlock(index, nextBlock);
          }}
        />
      </aside>
    </div>
  );
}
